import { Capacitor, registerPlugin } from '@capacitor/core'
import { App as CapApp } from '@capacitor/app'

const PERM_KEY = 'oem_call_log_perm'   // 'granted' | 'denied' | 'permanently_denied' | unset

export type CallLogPermStatus = 'granted' | 'denied' | 'permanently_denied' | 'not_asked'

interface CallLogPlugin {
  checkPermissions(): Promise<{ callLog: string; mediaAudio: string; storage: string }>
  requestPermissions(): Promise<{ callLog: string; mediaAudio: string; storage: string }>
}

const CallLog = registerPlugin<CallLogPlugin>('CallLog')

/** Read cached permission status (no native call). */
export function getCachedCallLogPermStatus(): CallLogPermStatus {
  if (!Capacitor.isNativePlatform()) return 'not_asked'
  try {
    const v = localStorage.getItem(PERM_KEY)
    if (v === 'granted') return 'granted'
    if (v === 'permanently_denied') return 'permanently_denied'
    if (v === 'denied') return 'denied'
  } catch { /* ignore */ }
  return 'not_asked'
}

function setCached(status: CallLogPermStatus) {
  try { localStorage.setItem(PERM_KEY, status) } catch { /* ignore */ }
}

function pluginResultToStatus(result: { callLog: string }): CallLogPermStatus {
  if (result.callLog === 'granted') return 'granted'
  if (result.callLog === 'prompt') return 'not_asked'
  if (result.callLog === 'denied') return 'permanently_denied'
  return 'denied'
}

export async function checkCallLogPermission(): Promise<CallLogPermStatus> {
  if (!Capacitor.isNativePlatform()) return 'not_asked'
  try {
    const result = await CallLog.checkPermissions()
    const status = pluginResultToStatus(result)
    setCached(status)
    return status
  } catch {
    return 'not_asked'
  }
}

export async function requestCallLogPermission(): Promise<CallLogPermStatus> {
  if (!Capacitor.isNativePlatform()) return 'not_asked'
  try {
    const result = await CallLog.requestPermissions()
    const status = pluginResultToStatus(result)
    setCached(status)
    return status
  } catch {
    return 'not_asked'
  }
}

/** Open Android app settings so the user can manually grant the permission. */
export async function openAppSettings(): Promise<void> {
  try {
    // Deep-link to this app's permission settings page. The package differs between
    // the production and dev apps, so read it from the running app.
    const { id: pkg } = await CapApp.getInfo()
    window.open(`android.settings.APPLICATION_DETAILS_SETTINGS:${pkg}`, '_system')
  } catch { /* ignore */ }
}
